import { useState } from 'react';
import { Link } from 'react-router-dom';
import InvestorCertificationModal from './InvestorCertificationModal';
import { openCookiePreferences } from '../../../components/feature/CookieConsent';

const Footer = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="footer" className="bg-gradient-to-br from-navy to-gray text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-10 md:py-12">
        {/* Top Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 lg:gap-10 mb-8 md:mb-10">
          {/* Company Info */}
          <div className="lg:col-span-1 flex flex-col items-center text-center">
            <img
              src="https://static.readdy.ai/image/102409a7b7d5861e272bf6d8b1888287/bef5ea38eecc1cdff2583b4b950b8fbb.png?w=800&h=300&q=100"
              alt="Dulair"
              className="h-20 md:h-24 w-auto mb-3"
            />
            <p className="text-white/70 text-xs leading-relaxed text-center">
              Preserving and growing family wealth since 1996
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-gold font-semibold text-sm mb-2.5">Quick Links</h4>
            <ul className="space-y-1.5">
              <li>
                <button
                  onClick={() => scrollToSection('home')}
                  className="text-white/70 hover:text-gold transition-colors text-xs cursor-pointer"
                >
                  Home
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('about')}
                  className="text-white/70 hover:text-gold transition-colors text-xs cursor-pointer"
                >
                  About Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('services')}
                  className="text-white/70 hover:text-gold transition-colors text-xs cursor-pointer"
                >
                  Our Services
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('portfolio')}
                  className="text-white/70 hover:text-gold transition-colors text-xs cursor-pointer"
                >
                  Portfolio
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-gold font-semibold text-sm mb-2.5">Our Services</h4>
            <ul className="space-y-1.5">
              <li className="text-white/70 text-xs">Property Investments</li>
              <li className="text-white/70 text-xs">Healthcare Ventures</li>
              <li className="text-white/70 text-xs">Strategic Investments</li>
              <li className="text-white/70 text-xs">Portfolio Management</li>
            </ul>
          </div>

          {/* Contact & Social */}
          <div>
            <h4 className="text-gold font-semibold text-sm mb-2.5">Connect With Us</h4>
            <div className="space-y-1.5 mb-3">
              <p className="text-white/70 text-xs leading-relaxed">
                Kings Head House<br />
                15 London End<br />
                Beaconsfield, Buckinghamshire<br />
                HP9 2HN
              </p>
              <p className="text-white/70 text-xs">+44 203 151 0000</p>
              <p className="text-white/70 text-xs break-words">enquiries@dulairpfo.com</p>
            </div>
            <div className="flex space-x-2.5">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center hover:bg-gold transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-linkedin-fill text-sm"></i>
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center hover:bg-gold transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-twitter-x-fill text-sm"></i>
              </a>
              <a
                href="mailto:enquiries@dulairpfo.com"
                className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center hover:bg-gold transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-mail-fill text-sm"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-white/15 pt-5">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-2.5 md:space-y-0">
            <div className="text-white/50 text-xs text-center md:text-left">
              © 2025 Dulair Private Family Office. All Rights Reserved.
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-5">
              <Link to="/privacy-policy" className="text-white/50 hover:text-gold text-xs transition-colors whitespace-nowrap cursor-pointer">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="text-white/50 hover:text-gold text-xs transition-colors whitespace-nowrap cursor-pointer">
                Terms of Service
              </Link>
              <Link to="/disclaimer" className="text-white/50 hover:text-gold text-xs transition-colors whitespace-nowrap cursor-pointer">
                Disclaimer
              </Link>
              <button
                onClick={openCookiePreferences}
                className="text-white/50 hover:text-gold text-xs transition-colors whitespace-nowrap cursor-pointer"
              >
                Manage Cookies
              </button>
            </div>
          </div>
        </div>

        {/* Regulatory Disclaimer */}
        <div className="border-t border-white/10 mt-5 pt-5">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-gold/70 text-sm"></i>
            </div>
            <div className="space-y-2">
              <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">
                <Link to="/disclaimer" className="hover:text-gold transition-colors cursor-pointer">Regulatory Disclaimer</Link>
              </h4>
              <p className="text-white/45 text-[11px] leading-relaxed">
                Dulair Private Family Office is <strong className="text-white/60">not authorised or regulated</strong> by the Financial Conduct Authority (FCA). We do not provide regulated financial advice, and nothing on this website should be construed as a personal recommendation or financial promotion directed at the general public.
              </p>
              <p className="text-white/45 text-[11px] leading-relaxed">
                Investment opportunities presented through this website are made available exclusively to individuals who qualify as <strong className="text-white/60">High Net Worth Individuals</strong> or <strong className="text-white/60">Sophisticated Investors</strong> under the Financial Services and Markets Act 2000 (Financial Promotion) Order 2005 (FPO). All investors must complete a self-certification process before accessing investment materials.
              </p>
              <p className="text-white/45 text-[11px] leading-relaxed">
                <strong className="text-white/60">Capital at risk:</strong> The value of investments can go down as well as up, and you may receive back less than your original investment. Past performance is not a reliable indicator of future results. You should seek independent professional advice before making any investment decision.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Bottom Image Section */}
      <div className="bg-navy py-8 md:py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6 items-center">
            <div className="lg:col-span-1">
              <div className="rounded-2xl overflow-hidden h-40 md:h-48">
                <img
                  src="https://public.readdy.ai/ai/img_res/6d029ea1bb34291f436799ad4a1c6e63.jpg"
                  alt="Dulair Excellence"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="lg:col-span-1 text-center lg:text-left px-2">
              <p className="font-serif text-lg md:text-xl lg:text-2xl text-white italic leading-relaxed">
                &ldquo;Excellence in wealth preservation and growth for discerning families&rdquo;
              </p>
            </div>
            <div className="lg:col-span-1">
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 md:p-5 border border-white/10">
                <h4 className="text-white text-sm font-semibold mb-2">Stay Informed</h4>
                <p className="text-white/60 text-xs mb-3">
                  Receive exclusive insights about investment opportunities
                </p>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="w-full px-4 py-2.5 bg-gold text-navy rounded-lg hover:opacity-90 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 font-medium text-xs"
                >
                  <i className="ri-shield-check-line"></i>
                  Register as Certified Investor
                </button>
                <p className="text-white/40 text-[10px] mt-2 text-center">
                  Self-certification required under FPO 2005
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Investor Certification Modal */}
      <InvestorCertificationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </footer>
  );
};

export default Footer;
